import type {
  DocumentPageMetadataUserEditEntry,
  DocumentPageMetadataUserEditValues,
} from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import type { SystemDocumentPageEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentPageSchemas'
import { mergePageMetadataValues } from 'features/knowledgeDocumentViewer/utils/effectiveMetadata'
import { buildGeneratedPageMetadataValues } from 'features/knowledgeDocumentViewer/utils/metadataUserEditWrite'
import { canConfirmPageMetadataEval } from 'features/knowledgeDocumentViewer/utils/pageMetadataEvalConfirm'
import {
  arePageMetadataLabelValuesEmpty,
  draftLabelsToPageValues,
  HIDDEN_PAGE_METADATA_LABELS,
  isPageMetadataEditableLabel,
  type PageMetadataDraftFields,
} from 'features/knowledgeDocumentViewer/utils/pageMetadataFieldKeys'
import {
  getPageMetadataCardFields,
  type MetadataDisplayField,
} from 'features/knowledgeDocumentViewer/utils/pageMetadataFields'
import { useCallback, useEffect, useMemo, useState } from 'react'

export type UsePageMetadataEvalFormArgs = {
  pageEntry: SystemDocumentPageEntry | undefined
  pageNum: number
  savedEvalEvent: DocumentPageMetadataUserEditEntry | undefined
  mixInValues?: DocumentPageMetadataUserEditValues
}

export type UsePageMetadataEvalFormResult = {
  editableFields: MetadataDisplayField[]
  values: DocumentPageMetadataUserEditValues
  generatedValuesAreEmpty: boolean
  shouldHaveMetadata: boolean | undefined
  canConfirm: boolean
  setFieldValue: (label: string, value: string) => void
  setShouldHaveMetadata: (value: boolean) => void
}

export function usePageMetadataEvalForm({
  pageEntry,
  pageNum,
  savedEvalEvent,
  mixInValues,
}: UsePageMetadataEvalFormArgs): UsePageMetadataEvalFormResult {
  const [draftFields, setDraftFields] = useState<PageMetadataDraftFields>({})
  const [draftShouldHaveMetadata, setShouldHaveMetadata] = useState<
    boolean | undefined
  >(undefined)

  const generatedValues = useMemo(
    () => buildGeneratedPageMetadataValues(pageEntry?.entry.data),
    [pageEntry?.entry.data]
  )
  const generatedValuesAreEmpty =
    arePageMetadataLabelValuesEmpty(generatedValues)

  useEffect(() => {
    setDraftFields({})
    setShouldHaveMetadata(undefined)
  }, [pageEntry?.entry.id, pageNum])

  const setFieldValue = useCallback((label: string, value: string) => {
    if (!isPageMetadataEditableLabel(label)) return
    setDraftFields((prev) => ({ ...prev, [label]: value }))
  }, [])

  const savedValues = savedEvalEvent?.entry.data.values
  const overlayValues = savedValues ?? mixInValues
  const shouldHaveMetadata =
    draftShouldHaveMetadata ??
    savedEvalEvent?.entry.data.should_have_metadata ??
    undefined
  const values = useMemo(() => {
    const baseline = mergePageMetadataValues(
      pageEntry?.entry.data,
      overlayValues
    )
    return draftLabelsToPageValues(draftFields, baseline)
  }, [draftFields, overlayValues, pageEntry?.entry.data])

  const canConfirm = canConfirmPageMetadataEval({
    generatedValuesAreEmpty,
    formValuesAreEmpty: arePageMetadataLabelValuesEmpty(values),
    shouldHaveMetadata,
  })

  const editableFields = getPageMetadataCardFields(
    pageEntry,
    pageNum,
    overlayValues
  )
    .filter((field) => !HIDDEN_PAGE_METADATA_LABELS.has(field.label))
    .map((field) => {
      const draftValue = isPageMetadataEditableLabel(field.label)
        ? draftFields[field.label]
        : undefined
      return {
        label: field.label,
        value: draftValue ?? field.value ?? '',
      }
    })

  return {
    editableFields,
    values,
    generatedValuesAreEmpty,
    shouldHaveMetadata,
    canConfirm,
    setFieldValue,
    setShouldHaveMetadata,
  }
}
